import { useEffect, useState } from 'react';
import { adSyncAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Search, 
  RefreshCw, 
  Users, 
  UserPlus, 
  CloudDownload, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  History,
  Loader2,
  Link2
} from 'lucide-react';

export default function ADSyncPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [syncLogs, setSyncLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [relinkingManagers, setRelinkingManagers] = useState(false);

  // Fetch AD sync status
  const fetchStatus = async () => {
    try {
      const res = await adSyncAPI.getStatus();
      setStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch AD sync status:', error);
      toast.error('Failed to load AD sync status');
    } finally {
      setLoading(false);
    }
  };

  // Search AD users
  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      toast.error('Please enter at least 2 characters to search');
      return;
    }
    
    setSearching(true);
    try {
      console.log('Searching for:', searchQuery);
      const res = await adSyncAPI.searchUsers(searchQuery);
      console.log('Search response:', res.data);
      setSearchResults(res.data.users || []);
      if (res.data.users?.length === 0) {
        toast.info('No users found matching your search');
      } else {
        toast.success(`Found ${res.data.users.length} users`);
      }
    } catch (error) {
      console.error('Search failed:', error);
      if (error.response?.status === 503) {
        toast.error('Azure AD sync is not configured. Please check your settings.');
      } else {
        toast.error('Search failed. Please try again.');
      }
    } finally {
      setSearching(false);
    }
  };

  // Preview bulk sync
  const handlePreviewSync = async () => {
    setPreviewLoading(true);
    try {
      const res = await adSyncAPI.previewBulkSync(includeDisabled);
      setSyncPreview(res.data);
    } catch (error) {
      console.error('Preview failed:', error);
      if (error.response?.status === 503) {
        toast.error('Azure AD sync is not configured');
      } else {
        toast.error('Failed to preview sync');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  // Execute bulk sync
  const handleExecuteSync = async () => {
    setSyncing(true);
    try {
      const res = await adSyncAPI.executeBulkSync(false, includeDisabled);
      const managersLinked = res.data.managers_linked || 0;
      toast.success(`Sync complete: ${res.data.created} created, ${res.data.updated} updated, ${res.data.deactivated} deactivated, ${managersLinked} managers linked`);
      setConfirmSyncOpen(false);
      setSyncPreview(null);
      fetchStatus();
      fetchLogs();
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Sync failed. Please check logs for details.');
    } finally {
      setSyncing(false);
    }
  };

  // Re-link managers from AD
  const handleRelinkManagers = async () => {
    setRelinkingManagers(true);
    try {
      const res = await adSyncAPI.relinkManagers();
      toast.success(`Manager relationships updated: ${res.data.managers_linked} linked from ${res.data.ad_users_processed} AD users`);
      fetchStatus();
    } catch (error) {
      console.error('Relink managers failed:', error);
      if (error.response?.status === 503) {
        toast.error('Azure AD sync is not configured');
      } else {
        toast.error('Failed to re-link managers');
      }
    } finally {
      setRelinkingManagers(false);
    }
  };

  // Import single user
  const handleImportUser = async () => {
    if (!selectedUser) return;
    
    setImporting(true);
    try {
      const res = await adSyncAPI.importUser({
        aad_object_id: selectedUser.aad_object_id,
        email: selectedUser.email,
        name: selectedUser.name,
        designation: selectedUser.designation,
        department: selectedUser.department,
        phone: selectedUser.phone,
        ad_status: selectedUser.ad_status
      });
      
      toast.success(`${selectedUser.name} ${res.data.action === 'created' ? 'imported' : 'updated'} successfully`);
      setImportDialogOpen(false);
      setSelectedUser(null);
      fetchStatus();
      
      // Update search results to show the user is now imported
      setSearchResults(prev => prev.map(u => 
        u.aad_object_id === selectedUser.aad_object_id 
          ? { ...u, already_imported: true }
          : u
      ));
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import user');
    } finally {
      setImporting(false);
    }
  };

  // Fetch sync logs
  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await adSyncAPI.getLogs(50);
      setSyncLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Active Directory Sync</h1>
          <p className="text-muted-foreground mt-1">
            Import and sync employees from Azure AD / Microsoft Entra
          </p>
        </div>
        <Button variant="outline" onClick={fetchStatus}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Status
        </Button>
      </div>

      {/* Status Alert */}
      {!status?.api_enabled && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Azure AD Not Configured</AlertTitle>
          <AlertDescription>
            Azure AD sync is not configured. Please set <code>MS_CLIENT_ID</code>, <code>MS_CLIENT_SECRET</code>, and <code>MS_TENANT_ID</code> environment variables.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Employees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-2xl font-bold">{status?.total_employees || 0}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Synced from AD
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold">{status?.ad_synced || 0}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              AD Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold">{status?.ad_active || 0}</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              AD Inactive
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <span className="text-2xl font-bold">{status?.ad_inactive || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last Sync Info */}
      {status?.last_bulk_sync && (
        <Card className="bg-muted/50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <History className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Last Bulk Sync</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(status.last_bulk_sync.timestamp)} by {status.last_bulk_sync.synced_by_name || 'System'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-600">+{status.last_bulk_sync.created} created</span>
                <span className="text-blue-600">{status.last_bulk_sync.updated} updated</span>
                <span className="text-red-600">{status.last_bulk_sync.deactivated} deactivated</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="search" className="space-y-4">
        <TabsList>
          <TabsTrigger value="search">
            <Search className="w-4 h-4 mr-2" />
            Search & Import
          </TabsTrigger>
          <TabsTrigger value="bulk">
            <CloudDownload className="w-4 h-4 mr-2" />
            Bulk Sync
          </TabsTrigger>
          <TabsTrigger value="logs" onClick={fetchLogs}>
            <History className="w-4 h-4 mr-2" />
            Sync Logs
          </TabsTrigger>
        </TabsList>

        {/* Search & Import Tab */}
        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Search Azure AD Users</CardTitle>
              <CardDescription>
                Search for users by name, email, or department to import individually
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or department..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                    disabled={!status?.api_enabled}
                  />
                </div>
                <Button onClick={handleSearch} disabled={searching || !status?.api_enabled}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                </Button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Manager</TableHead>
                      <TableHead>AD Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.map((user) => (
                      <TableRow key={user.aad_object_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-xs text-muted-foreground">{user.designation || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.department || '-'}</TableCell>
                        <TableCell>
                          {user.manager_name ? (
                            <div>
                              <p className="text-sm">{user.manager_name}</p>
                              <p className="text-xs text-muted-foreground">{user.manager_email}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.ad_status === 'active' ? 'default' : 'secondary'}>
                            {user.ad_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {user.already_imported ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Imported
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setImportDialogOpen(true);
                              }}
                            >
                              <UserPlus className="w-4 h-4 mr-1" />
                              Import
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Sync Tab */}
        <TabsContent value="bulk" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Sync from Azure AD</CardTitle>
              <CardDescription>
                Sync all users from Azure AD. This will create new employees, update existing ones, and mark removed users as inactive.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={includeDisabled}
                      onCheckedChange={setIncludeDisabled}
                      id="include-disabled"
                    />
                    <Label htmlFor="include-disabled">Include disabled AD accounts</Label>
                  </div>
                  <Button 
                    onClick={handlePreviewSync} 
                    variant="outline"
                    disabled={previewLoading || !status?.api_enabled}
                  >
                    {previewLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Preview Changes
                  </Button>
                </div>
                <Button 
                  onClick={handleRelinkManagers} 
                  variant="secondary"
                  disabled={relinkingManagers || !status?.api_enabled}
                >
                  {relinkingManagers ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Users className="w-4 h-4 mr-2" />
                  )}
                  Re-link Managers
                </Button>
              </div>

              {/* Sync Preview */}
              {syncPreview && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Sync Preview</h4>
                    <Badge variant="outline">
                      {syncPreview.ad_users_found} users found in AD
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <UserPlus className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-2xl font-bold text-green-700">
                              {syncPreview.summary?.create_count || 0}
                            </p>
                            <p className="text-sm text-green-600">To Create</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-2xl font-bold text-blue-700">
                              {syncPreview.summary?.update_count || 0}
                            </p>
                            <p className="text-sm text-blue-600">To Update</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-5 w-5 text-red-600" />
                          <div>
                            <p className="text-2xl font-bold text-red-700">
                              {syncPreview.summary?.deactivate_count || 0}
                            </p>
                            <p className="text-sm text-red-600">To Deactivate</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Preview Details */}
                  {syncPreview.to_create?.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="font-medium text-green-700">New Employees to Create:</h5>
                      <div className="max-h-48 overflow-y-auto border rounded-md p-2">
                        {syncPreview.to_create.slice(0, 15).map((user, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1.5 text-sm border-b last:border-0">
                            <div className="flex items-center gap-2">
                              <UserPlus className="w-3 h-3 text-green-600 flex-shrink-0" />
                              <div>
                                <span className="font-medium">{user.name}</span>
                                <span className="text-muted-foreground ml-1">({user.email})</span>
                              </div>
                            </div>
                            {user.manager_name && (
                              <span className="text-xs text-muted-foreground">
                                Manager: {user.manager_name}
                              </span>
                            )}
                          </div>
                        ))}
                        {syncPreview.to_create.length > 15 && (
                          <p className="text-sm text-muted-foreground pt-2">
                            ... and {syncPreview.to_create.length - 15} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {syncPreview.to_deactivate?.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="font-medium text-red-700">Employees to Deactivate:</h5>
                      <div className="max-h-40 overflow-y-auto border rounded-md p-2">
                        {syncPreview.to_deactivate.map((user, idx) => (
                          <div key={idx} className="flex items-center gap-2 py-1 text-sm">
                            <XCircle className="w-3 h-3 text-red-600" />
                            <span>{user.name}</span>
                            <span className="text-muted-foreground">({user.email})</span>
                            <span className="text-xs text-red-500">- {user.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={() => setConfirmSyncOpen(true)}
                      disabled={
                        (syncPreview.summary?.create_count === 0 && 
                         syncPreview.summary?.update_count === 0 && 
                         syncPreview.summary?.deactivate_count === 0)
                      }
                    >
                      <CloudDownload className="w-4 h-4 mr-2" />
                      Execute Sync
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sync History</CardTitle>
              <CardDescription>
                View the history of AD sync operations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logsLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : syncLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No sync logs found
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Operation</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Synced By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {formatDate(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={log.type === 'bulk_sync' ? 'default' : 'outline'}>
                            {log.type === 'bulk_sync' ? 'Bulk' : 'Single'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.type === 'bulk_sync' ? (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-green-600">+{log.created}</span>
                              <span className="text-blue-600">{log.updated} upd</span>
                              <span className="text-red-600">{log.deactivated} deact</span>
                            </div>
                          ) : (
                            <span className="capitalize">{log.operation}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.type === 'bulk_sync' ? (
                            <span>{log.ad_users_found} AD users found</span>
                          ) : (
                            <span>{log.employee_name}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {log.synced_by_name || 'System'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import User Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import User from Azure AD</DialogTitle>
            <DialogDescription>
              Import this user as a new employee or update existing record
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{selectedUser.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Department</Label>
                  <p className="font-medium">{selectedUser.department || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Designation</Label>
                  <p className="font-medium">{selectedUser.designation || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-medium">{selectedUser.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">AD Status</Label>
                  <Badge variant={selectedUser.ad_status === 'active' ? 'default' : 'secondary'}>
                    {selectedUser.ad_status}
                  </Badge>
                </div>
              </div>
              
              {/* Manager Info */}
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Manager (from AD)</Label>
                {selectedUser.manager_name ? (
                  <div className="mt-1 p-3 bg-muted/50 rounded-md">
                    <p className="font-medium">{selectedUser.manager_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedUser.manager_email}</p>
                  </div>
                ) : (
                  <p className="mt-1 text-muted-foreground">No manager assigned in AD</p>
                )}
              </div>
              
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  If an employee with this email already exists, their record will be updated and linked to this AD account. Manager relationship will be synced if the manager exists in the system.
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleImportUser} disabled={importing}>
              {importing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Import User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Bulk Sync Dialog */}
      <Dialog open={confirmSyncOpen} onOpenChange={setConfirmSyncOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Sync</DialogTitle>
            <DialogDescription>
              Are you sure you want to execute this sync? This will make the following changes:
            </DialogDescription>
          </DialogHeader>
          
          {syncPreview && (
            <div className="py-4 space-y-2">
              <p className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span><strong>{syncPreview.summary?.create_count || 0}</strong> new employees will be created</span>
              </p>
              <p className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-600" />
                <span><strong>{syncPreview.summary?.update_count || 0}</strong> employees will be updated</span>
              </p>
              <p className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span><strong>{syncPreview.summary?.deactivate_count || 0}</strong> employees will be marked inactive</span>
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSyncOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExecuteSync} disabled={syncing}>
              {syncing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CloudDownload className="w-4 h-4 mr-2" />
              )}
              Confirm & Sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
